// Only define GenericQuiz if it hasn't been defined yet
if (typeof customElements.get('generic-quiz') === 'undefined') {
  
  // ----------------------------------------------------------------------------------------------------------------
  // CALIFORNIA SUBSCRIPTION TERMS - Geolocation Detection
  // Detects if user is in California and caches the result
  // ----------------------------------------------------------------------------------------------------------------
  const CA_STORAGE_KEY = 'athena_user_state';

  async function detectCaliforniaResident() {
    // Check if we already have the state cached
    const cachedState = localStorage.getItem(CA_STORAGE_KEY);
    if (cachedState) {
      return cachedState === 'CA';
    }
    
    try {
      // Use ipapi.co for state-level geolocation
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      
      // Check if the user is in California, USA
      if (data.country_code === 'US' && data.region_code) {
        localStorage.setItem(CA_STORAGE_KEY, data.region_code);
        return data.region_code === 'CA';
      }
      
      // Not in US, store that information
      localStorage.setItem(CA_STORAGE_KEY, 'NON_US');
      return false;
    } catch (error) {
      console.warn('Could not detect user location:', error);
      // On error, don't show the checkbox (fail gracefully)
      return false;
    }
  }

  class GenericQuiz extends HTMLElement {
    constructor() {
      super();
      this.state = {};
      this.bindings = new Map();
      this.analytics = null;
      
      // California subscription terms state
      this.caTermsAgreed = false;
      this.isCaliforniaResident = false; // Will be set by geolocation detection
      
      this._observer = new MutationObserver(mutations => {
        mutations.forEach(m => {
          if (m.type === 'attributes' && m.attributeName.startsWith('data-')) {
            this.updateState(m.attributeName, this.getAttribute(m.attributeName));
          }
        });
      });

      // ----------------------------------------------------------------------------------------------------------------
      // CALIFORNIA GEOLOCATION DETECTION
      // Detects if user is in California and shows subscription terms checkbox accordingly
      // ----------------------------------------------------------------------------------------------------------------
      this.initCaliforniaDetection();

      // console.log('GenericQuiz constructor');
    }

    // Initialize California detection
    async initCaliforniaDetection() {
      this.isCaliforniaResident = await detectCaliforniaResident();
    }

  static get observedAttributes() {
    // Optional: using MutationObserver instead
    return [];
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this._observer.disconnect();
    if (this._onPdpVariantChanged) {
      document.removeEventListener('variant:changed', this._onPdpVariantChanged);
    }
  }

  // Read initial data-* attributes into state
  init() {
    Array.from(this.attributes).forEach(attr => {
      if (attr.name.startsWith('data-')) {
        this.state[this._toCamel(attr.name.replace('data-', ''))] = attr.value;
      }
    });

    // console.log('state', this.state);

    // Count total steps
    this.state.totalSteps = this.querySelectorAll('.quiz-step').length;

    // Initialize annual upsell state
    this.state.yearlyBladesSelected = false;
    this.state.bladeShipsOpen = false;

    // Initialize analytics if available
    this.initializeAnalytics();

    // Initialize cross-sell functionality
    this.initializeCrossSell();

    // Listen for PDP variant changes and map by variant name to this quiz's main product
    this._onPdpVariantChanged = (event) => {
      const variant = event && event.detail && event.detail.variant ? event.detail.variant : null;
      if (!variant || !variant.title) return;
      // Update by name; mapping to this quiz's main product will occur via updateState handler
      this.setAttribute('data-selected-main-variant-name', variant.title);
    };
    document.addEventListener('variant:changed', this._onPdpVariantChanged);

    // If an initial variant name is present, map it to the correct ID for this quiz product
    if (this.state.selectedMainVariantName) {
      const mappedId = this._mapVariantNameToId(this.state.selectedMainVariantName);
      if (mappedId) {
        this.state.selectedMainVariantId = mappedId.toString();
        this.setAttribute('data-selected-main-variant-id', mappedId);
      }
    }

    // Initialize default selling plan for Canada blades quiz (3 months)
    this.initializeDefaultSellingPlanForBladesQuizCA();

    this.render();
    this._observer.observe(this, { attributes: true });
  }

  // Initialize analytics if available
  initializeAnalytics() {
    if (window.QuizAnalytics) {
      this.analytics = new window.QuizAnalytics(this);
    }
  }

  // Initialize cross-sell functionality
  initializeCrossSell() {
    const crossSellData = this.querySelector('[data-cross-sell-data]');
    if (crossSellData) {
      try {
        this.crossSellData = JSON.parse(crossSellData.textContent);
      } catch (e) {
        console.warn('Failed to parse cross-sell data:', e);
      }
    }
  }

  // Initialize default selling plan for Canada blades quiz only
  initializeDefaultSellingPlanForBladesQuizCA() {
    try {
      console.log('[DEFAULT PLAN] Method called, quiz ID:', this.id);

      // ONLY run for Canada blades quiz - do not affect any other quiz
      if (this.id !== 'blades_quiz_ca') {
        console.log('[DEFAULT PLAN] Not Canada blades quiz, skipping');
        return;
      }

      console.log('[DEFAULT PLAN] Is Canada blades quiz, continuing...');
      console.log('[DEFAULT PLAN] Current selling plan:', this.state.selectedMainSellingPlanId || 'none');

      // Get the main product data
      const productData = this._getMainProductDataRaw();
      console.log('[DEFAULT PLAN] Product data:', productData ? 'found' : 'NOT FOUND');

      if (!productData || !productData.selling_plan_groups) {
        console.log('[DEFAULT PLAN] No product data or selling plan groups');
        return;
      }

      console.log('[DEFAULT PLAN] Selling plan groups:', productData.selling_plan_groups.length);

      // Find the 3-month selling plan ("a few times a week")
      let threeMonthPlan = null;
      for (const group of productData.selling_plan_groups) {
        console.log('[DEFAULT PLAN] Checking group, selling plans:', group.selling_plans ? group.selling_plans.length : 0);
        if (group.selling_plans) {
          threeMonthPlan = group.selling_plans.find(plan => {
            const planName = (plan.name || '').toLowerCase();
            const matches = planName.includes('every 3 months') || planName.includes('3 months') || planName.includes('3-month');
            console.log('[DEFAULT PLAN] Checking plan:', plan.name, 'Matches:', matches);
            return matches;
          });
          if (threeMonthPlan) break;
        }
      }

      // Set the 3-month plan as default if found
      if (threeMonthPlan) {
        const planId = threeMonthPlan.id.toString();
        console.log('[DEFAULT PLAN] ✅ Found 3-month plan:', threeMonthPlan.name, 'ID:', planId);
        this.state.selectedMainSellingPlanId = planId;
        this.setAttribute('data-selected-main-selling-plan-id', planId);
        console.log('[DEFAULT PLAN] ✅ Default selling plan set successfully');
      } else {
        console.log('[DEFAULT PLAN] ❌ Could not find 3-month selling plan');
      }
    } catch (error) {
      console.error('[DEFAULT PLAN] ❌ ERROR:', error);
    }
  }

  // Called when a data-* attribute changes
  updateState(name, value) {
    const key = this._toCamel(name.replace('data-', ''));
    this.state[key] = value;
    // console.log('state', this.state);

    // If variant name changes, map it to the corresponding variant ID of the quiz's main product
    if (key === 'selectedMainVariantName') {
      const mappedId = this._mapVariantNameToId(value);
      if (mappedId) {
        const mappedIdStr = mappedId.toString();
        if (this.state.selectedMainVariantId !== mappedIdStr) {
          this.state.selectedMainVariantId = mappedIdStr;
          this.setAttribute('data-selected-main-variant-id', mappedIdStr);
        }
        // Ensure UI reflects the new selection immediately
        this.updateVariantSelectors();
        this.updateSellingPlanSelectors();
      }
    }

    // Log complete quiz state for debugging
    this.logCompleteQuizState();

    // Trigger custom event for external listeners
    this.dispatchEvent(new CustomEvent('state-changed', {
      detail: { key, value, dataAttribute: name }
    }));

    // Update variant selectors if selectedMainVariantId changed
    if (key === 'selectedMainVariantId') {
      this.updateVariantSelectors();
      this.updateSellingPlanSelectors();
    }

    if (key === 'selectedMainSellingPlanId') {
      this.updateSellingPlanSelectors();
    }

    // Update recap if it's currently visible and relevant state changed
    const recapStep = this.querySelector('.quiz-step[data-step="' + this.state.currentStep + '"] .quiz-recap');
    if (recapStep && ['selectedMainVariantId', 'selectedMainSellingPlanId', 'annualProductSelected', 'selectedCrossSellProductId', 'selectedCrossSellVariantId', 'selectedCrossSellOption', 'selectedCrossSellSellingPlanId'].includes(key)) {
      // console.log('🔄 State changed, updating recap...', key, value);
        this.renderRecap();
    }
  }

  // Log complete quiz state for debugging
  logCompleteQuizState() {
    const mainProductState = {
      selectedMainVariantId: this.state.selectedMainVariantId,
      selectedMainSellingPlanId: this.state.selectedMainSellingPlanId,
      annualProductSelected: this.state.annualProductSelected
    };

    const crossSellState = this.getCrossSellState();

    // console.log('📊 Complete Quiz State:', {
    //   currentStep: this.state.currentStep,
    //   totalSteps: this.state.totalSteps,
    //   mainProduct: mainProductState,
    //   crossSell: crossSellState,
    //   testData: {
    //     testActive: this.state.testActive,
    //     userVariant: this.state.userVariant,
    //     uniqueTestId: this.state.uniqueTestId
    //   }
    // });
  }

  // Render modal content directly into light DOM
  render() {
    // Bind buttons
    this.querySelector('.quiz-close-button').onclick = () => this.close();
    // Note: Next button binding is handled dynamically in updateButtonStates()
    this.querySelector('.quiz-back-button').onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.prevStep();
    };

    // Setup input bindings
    // this.setupInputBindings();
    this.setupBindings();

    // Update step visibility and button states
    this.updateStepDisplay();
    this.updateButtonStates();
    this.updateProgressTracker();
    this.updateVariantSelectors();
    this.updateSellingPlanSelectors();

    // Setup cross-sell functionality
    this.setupCrossSell();
  }

  // Step navigation methods
  nextStep() {
    if (this.state.currentStep < this.state.totalSteps) {
      this.state.currentStep++;
      this.updateStepDisplay();
      this.updateButtonStates();
      this.updateProgressTracker();
      
      // Log quiz state when navigating
      // console.log(`📍 Navigated to step ${this.state.currentStep}:`);
      this.logCompleteQuizState();
    }
  }

  prevStep() {
    if (this.state.currentStep > 1) {
      this.state.currentStep--;
      this.updateStepDisplay();
      this.updateButtonStates();
      this.updateProgressTracker();
      
      // Log quiz state when navigating
      // console.log(`📍 Navigated back to step ${this.state.currentStep}:`);
      this.logCompleteQuizState();
    }
  }

  // Update which step is visible
  updateStepDisplay() {
    const steps = this.querySelectorAll('.quiz-step');
    
    steps.forEach((step, index) => {
      const stepNumber = parseInt(step.getAttribute('data-step'));
      if (stepNumber === this.state.currentStep) {
        step.style.display = 'block';

        // Check if this step contains cross-sell elements and initialize state
        const crossSellElements = step.querySelector('.quiz-cross-sell');
        if (crossSellElements) {
          // console.log('🛒 Cross-sell step detected, initializing state...');
          // Small delay to ensure DOM is ready
          this.setupCrossSellForCurrentStep();
        }

        // Check if this step contains recap elements and render recap
        const recapElements = step.querySelector('.quiz-recap');
        if (recapElements) {
          // console.log('📋 Recap step detected, rendering recap...');
          // Small delay to ensure DOM is ready
          this.renderRecap();
        }

        // Track step view with analytics
        if (this.analytics) {
          const variantGroup = step.getAttribute('data-variant-group');
          const variantType = step.getAttribute('data-variant-type');
          const isTestActive = step.getAttribute('data-test-active') === 'true';
          const blockId = step.getAttribute('data-block-id');

          const stepTypeElement = step.querySelector('[class*="quiz-"]');
          const stepType = stepTypeElement ?
            stepTypeElement.className.match(/quiz-([a-z-]+)/)[1] :
            'unknown';

          this.analytics.trackStepView(
            stepNumber,
            stepType,
            blockId,
            {
              testActive: isTestActive,
              variant: variantType || 'control',
              variantGroup: variantGroup || 'no_group'
            }
          );
        }
      } else {
        step.style.display = 'none';
      }
    });
  }

  // Update button states based on current step
  updateButtonStates() {
    const backButton = this.querySelector('.quiz-back-button');
    const nextButtonContainer = this.querySelector('.quiz-next-button');
    const nextButton = nextButtonContainer.querySelector('button');

    // Back button: disabled on first step
    if (this.state.currentStep === 1) {
      backButton.style.opacity = '0.5';
      backButton.style.cursor = 'not-allowed';
      backButton.style.pointerEvents = 'none';
    } else {
      backButton.style.opacity = '1';
      backButton.style.cursor = 'pointer';
      backButton.style.pointerEvents = 'auto';
    }

    // Check if current step is a cross-sell step
    const currentStepElement = this.querySelector(`.quiz-step[data-step="${this.state.currentStep}"]`);
    const isCrossSellStep = currentStepElement && currentStepElement.querySelector('.quiz-cross-sell');
    const isLastStep = this.state.currentStep === this.state.totalSteps;

    if (isCrossSellStep) {
      // Cross-sell step: show "Add To Plan" + "Skip" buttons
      nextButtonContainer.innerHTML = `
        <button class="dss-btn dss-fs-12 dss-tt-uppercase dss-btn-navy quiz-add-to-plan-button">Add To Plan</button>
        <button class="quiz-skip-button dss-fs-12">Skip</button>
      `;
      
      // Add event listeners for the new buttons
      const addToPlanButton = nextButtonContainer.querySelector('.quiz-add-to-plan-button');
      const skipButton = nextButtonContainer.querySelector('.quiz-skip-button');
      
      addToPlanButton.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.addToPlan();
      };
      skipButton.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.skipUpsell();
      };
      
    } else if (isLastStep) {
      // Last step: "Add To Cart" button
      nextButtonContainer.innerHTML = `
        <button class="dss-btn dss-fs-12 dss-tt-uppercase dss-btn-navy quiz-final-atc">Add To Cart</button>
      `;
      nextButtonContainer.querySelector('button').onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.addToCart();
      };
      
    } else {
      // Regular step: "Next Step" button
      nextButtonContainer.innerHTML = `
        <button class="dss-btn dss-fs-12 dss-tt-uppercase dss-btn-navy">Next Step</button>
      `;
      nextButtonContainer.querySelector('button').onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.nextStep();
      };
    }
  }

  // Add to plan functionality for cross-sell
  addToPlan() {
          // console.log('🛒 Adding cross-sell to plan...');
    
    const crossSellState = this.getCrossSellState();
          // console.log('🔍 Cross-sell state at add to plan:', crossSellState);
    
    if (crossSellState.isSelected) {
      // console.log('✅ Cross-sell product added to plan:', crossSellState);
      
      // Ensure the state is properly set and will persist
      // console.log('📝 Current state values:', {
      //   productId: this.state.selectedCrossSellProductId,
      //   variantId: this.state.selectedCrossSellVariantId,
      //   option: this.state.selectedCrossSellOption,
      //   sellingPlanId: this.state.selectedCrossSellSellingPlanId
      // });
      
      // Track analytics if available
      if (this.analytics) {
        const crossSellProduct = this.querySelector('.quiz-cross-sell__upsell');
        if (crossSellProduct) {
          this.analytics.trackCrossSellClick(crossSellProduct);
        }
      }
    } else {
      console.warn('⚠️ No cross-sell product selected');
      // console.log('🔍 Debug - state keys:', Object.keys(this.state));
      // console.log('🔍 Debug - state values:', {
      //   productId: this.state.selectedCrossSellProductId,
      //   variantId: this.state.selectedCrossSellVariantId,
      //   option: this.state.selectedCrossSellOption,
      //   sellingPlanId: this.state.selectedCrossSellSellingPlanId
      // });
    }
    
    // Continue to next step
    this.nextStep();
  }

  // Skip upsell functionality for cross-sell
  skipUpsell() {
    // console.log('⏭️ Skipping cross-sell upsell...');
    
    // Clear cross-sell selections
    this.setAttribute('data-selected-cross-sell-product-id', '');
    this.setAttribute('data-selected-cross-sell-variant-id', '');
    this.setAttribute('data-selected-cross-sell-option', '');
    this.setAttribute('data-selected-cross-sell-selling-plan-id', '');
    
    // Track analytics if available
    if (this.analytics) {
      this.analytics.trackInteraction('cross_sell_skip', {
        currentStep: this.state.currentStep
      });
    }
    
    // Continue to next step
    this.nextStep();
  }

  // Update progress tracker indicators
  updateProgressTracker() {
    const progressTracker = this.querySelector('.quiz-progress-tracker');
    const currentStepElement = this.querySelector(`.quiz-step[data-step="${this.state.currentStep}"]`);
    const isRecapStep = currentStepElement && currentStepElement.querySelector('.quiz-recap');
    
    if (isRecapStep) {
      // Hide progress tracker on recap step
      progressTracker.style.display = 'none';
    } else {
      // Show progress tracker and update indicators
      progressTracker.style.display = 'flex';
      const progressSteps = this.querySelectorAll('.quiz-progress-step');
      
      progressSteps.forEach((step, index) => {
        const stepNumber = parseInt(step.getAttribute('data-step'));
        const isActive = stepNumber <= this.state.currentStep;
        
        if (isActive) {
          step.classList.add('active');
        } else {
          step.classList.remove('active');
        }
      });
    }
  }

  updateVariantSelectors() {
    const variantSelectors = this.querySelectorAll('.quiz-variant-selector');
    variantSelectors.forEach(selector => {
      const variantId = selector.getAttribute('data-variant-id');
      if (variantId === this.state.selectedMainVariantId) {
        selector.classList.add('active');
      } else {
        selector.classList.remove('active');
      }
    });
  }

  updateSellingPlanSelectors() {
    const sellingPlanSelectors = this.querySelectorAll('.quiz-selling-plan-option');
    sellingPlanSelectors.forEach(selector => {
      const sellingPlanId = selector.getAttribute('data-selling-plan-id');
      const variantId = selector.getAttribute('data-variant-id');

      if (variantId === 'all_variants') {
        // setup for annual selling plan selector
        if (this.state.annualProductSelected === 'true') {
          selector.classList.add('active');
        } else {
          selector.classList.remove('active');
        }

        selector.classList.remove('hide');
      } 
      else {
        // setup for main selling plan selector
        if (sellingPlanId === this.state.selectedMainSellingPlanId) {
          selector.classList.add('active');
        } else {
          selector.classList.remove('active');
        }

        if (variantId === this.state.selectedMainVariantId) {
          selector.classList.remove('hide');
        } else {
          selector.classList.add('hide');
        }
      }

      
    });
  }

  // Show the quiz modal
  open(options = {}) {
    this.setAttribute('open', '');
    // Reset to first step when opening
    this.state.currentStep = 1;

    // Check for variant query parameter in URL and update selected variant if present
    this.checkUrlVariantParameter(options.variantId);

    // Ensure progress tracker is visible when opening quiz
    const progressTracker = this.querySelector('.quiz-progress-tracker');
    if (progressTracker) {
      progressTracker.style.display = 'flex';
    }

    this.updateStepDisplay();
    this.updateButtonStates();
    this.updateProgressTracker();
    document.body.classList.add('quiz-open-no-scroll');

    // Track quiz open
    if (this.analytics) {
      this.analytics.trackQuizStart();
    }
  }

  // Check for variant query parameter in URL and update selected variant if present
  checkUrlVariantParameter(selectedVariantId) {
    let variantId = selectedVariantId;
    if (!variantId) {
      const urlParams = new URLSearchParams(window.location.search);
      variantId = urlParams.get('variant');
    }

    if (variantId && variantId !== 'null' && variantId !== '') {
      // Only accept the URL variant if it exists on the QUIZ'S main product.
      const productData = this._getMainProductDataRaw();
      if (productData && Array.isArray(productData.variants)) {
        const match = productData.variants.find(v => v.id.toString() === variantId);
        if (match) {
          // Prefer name as source-of-truth; set name and let mapping derive ID
          this.setAttribute('data-selected-main-variant-name', match.title || '');
          const mappedId = match.id.toString();
          this.state.selectedMainVariantId = mappedId;
          this.setAttribute('data-selected-main-variant-id', mappedId);
          this.updateVariantSelectors();
          this.updateSellingPlanSelectors();
        } else {
          // Ignore URL variant: it belongs to PDP product, not quiz product
        }
      }
    }
  }

  // Hide the quiz modal
  close() {
    this.removeAttribute('open');
    document.body.classList.remove('quiz-open-no-scroll');

    // Track abandonment if not completed
    if (this.analytics && this.state.currentStep < this.state.totalSteps) {
      this.analytics.trackQuizAbandon(this.state.currentStep);
    }
  }

  // Add all quiz selections to cart and close modal
  async addToCart() {
    // console.log('🛒 Starting add to cart process...');
    
    // Validate California terms if required
    if (!this.validateCaTerms()) {
      return;
    }
    
    try {
      // Build the complete cart from the current quiz state
      const cart = this.buildRecapCart();
      // console.log('🛒 Cart to add:', cart);
      
      if (cart.length === 0) {
        console.warn('⚠️ No items in cart to add');
        return;
      }
      
      // Convert cart items to Shopify cart format
      const cartItems = this.buildShopifyCartItems(cart);
      // console.log('🛒 Shopify cart items:', cartItems);
      
      // Add items to cart using Shopify's bulk add endpoint
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items: cartItems })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Cart add failed: ${errorData.message || response.statusText}`);
      }

      const cartData = await response.json();
      // console.log('✅ Successfully added to cart:', cartData);
      
      // Track quiz completion and successful cart add
      if (this.analytics) {
        this.analytics.trackQuizComplete({
          itemCount: cartItems.length,
          totalValue: cartItems.reduce((sum, item) => sum + (item.price || 0) * item.quantity, 0),
          hasMainProduct: cart.some(item => item.type === 'main'),
          hasCrossSell: cart.some(item => item.type === 'cross-sell'),
          hasBlades: cart.some(item => item.type === 'blades'),
          isAnnualPlan: this.state.yearlyBladesSelected || this.state.annualProductSelected === 'true'
        });
        
        this.analytics.trackInteraction('quiz_cart_add_success', {
          itemCount: cartItems.length,
          totalValue: cartItems.reduce((sum, item) => sum + (item.price || 0) * item.quantity, 0)
        });
      }

      // Fire standard add_to_cart event for quiz main ATC button
      if (window.SiteAnalytics) {
        const mainItem = cart.find(item => item.type === 'main');
        if (mainItem) {
          window.SiteAnalytics.trackAddToCart({
            item_id:          mainItem.variantId || '',
            item_name:        mainItem.title,
            item_variant:     mainItem.subtitle,
            source_component: 'quiz_main',
            price:            (mainItem.price || 0) / 100,
            position:         null,
            selling_plan_id:  mainItem.sellingPlan?.id || null,
            quiz_id:          this.id
          });
        }

        const crossSellItem = cart.find(item => item.type === 'cross-sell');
        if (crossSellItem) {
          const crossSellDataEl = this.querySelector('[data-cross-sell-data]');
          const crossSellVariantTitle = crossSellDataEl ? JSON.parse(crossSellDataEl.textContent).variantTitle || '' : '';
          window.SiteAnalytics.trackAddToCart({
            item_id:          crossSellItem.variantId || '',
            item_name:        crossSellItem.title,
            item_variant:     crossSellVariantTitle,
            source_component: 'quiz_cross_sell',
            price:            (crossSellItem.price || 0) / 100,
            position:         null,
            selling_plan_id:  crossSellItem.sellingPlan?.id || null,
            quiz_id:          this.id
          });
        }
      }
      
      // Dispatch success event
      this.dispatchEvent(new CustomEvent('cart-add-success', { 
        detail: { 
          cartData, 
          addedItems: cartItems,
          quizCart: cart
        }
      }));

      // Get the cart data to update the UI
      const cartResponse = await fetch('/cart.js');
      const latestCartData = await cartResponse.json();
      
      // Close the quiz modal
      this.close();
      
      // Trigger cart drawer/notification if available
      this.triggerCartNotification(latestCartData);
      
    } catch (err) {
      console.error('❌ Cart add failed:', err);
      
      // Track error
      if (this.analytics) {
        this.analytics.trackInteraction('quiz_cart_add_error', {
          error: err.message
        });
      }
      
      // Dispatch error event
      this.dispatchEvent(new CustomEvent('cart-add-error', { detail: err }));
      
      // Show user-friendly error message
      this.showCartError(err.message);
    }
  }

  // Convert quiz cart items to Shopify cart format
  buildShopifyCartItems(cart) {
    const items = [];
    const isAnnualSelected = this.state.yearlyBladesSelected || this.state.annualProductSelected === 'true';
    
    cart.forEach(cartItem => {
      // Skip blade items for non-annual plans - they're display-only in recap
      if (cartItem.type === 'blades' && !isAnnualSelected) {
        // console.log(`⏭️ Skipping ${cartItem.type}: ${cartItem.title} (non-annual plan - applied to razor instead)`);
        return;
      }

              // console.log("RIGHT BEFORE SHOPIFY ITEM");
        // console.log("cartItem", cartItem);
      
      const shopifyItem = {
        id: cartItem.variantId,
        quantity: cartItem.quantity || 1
      };
      
      // Add selling plan if present
      if (cartItem.sellingPlan && cartItem.sellingPlan.id) {
        shopifyItem.selling_plan = cartItem.sellingPlan.id;
      }
      
      // Add properties for grouping and identification
      shopifyItem.properties = this.getPropertiesForCartItem(cartItem);
      
      items.push(shopifyItem);
      
              // console.log(`📦 Added ${cartItem.type}: ${cartItem.title}`, shopifyItem);
    });
    
    return items;
  }



  // Get properties for cart item identification and grouping
  getPropertiesForCartItem(cartItem) {
    const randomId = Math.random().toString(36).substring(2, 12);
    const properties = {
      '_quiz_item': 'true',
      '_quiz_id': this.id || 'generic-quiz',
      '_quiz_session': randomId
    };
    
    // Add type-specific properties
    switch (cartItem.type) {
      case 'main':
        properties['_main_product'] = 'true';
        properties['_quiz_razor'] = randomId;
        // Add _razor_kit_frequency property if main product has a selling plan
        if (cartItem.sellingPlan && cartItem.sellingPlan.id) {
          properties['_razor_kit_frequency'] = 'true';
        }
        break;
      case 'cross-sell':
        properties['_cross_sell'] = 'true';
        properties['_quiz_cross_sell'] = randomId;
        break;
      case 'blades':
        properties['_blades'] = 'true';
        properties['_quiz_blades'] = randomId;
        if (this.state.yearlyBladesSelected || this.state.annualProductSelected === 'true') {
          properties['_annual_plan'] = 'true';
        }
        break;
    }
    
    return properties;
  }

  // Trigger cart drawer or notification
  triggerCartNotification(cartData) {
          // console.log('🔔 Triggering cart open...');
    
    document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
      bubbles: true,
      detail: { source: 'quiz' }
    }));

    document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
      bubbles: true,
      detail: {
        cart: cartData
      }
    }));

    // Try to open mini-cart if available
    const miniCart = document.getElementById('mini-cart');
    if (miniCart && miniCart.open !== undefined) {
      miniCart.open = true;
    }
    
    // Try to trigger cart drawer if available
    const cartDrawer = document.querySelector('cart-drawer, .cart-drawer');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    }
  }
  // Show user-friendly error message
  showCartError(message) {
    // You could implement a toast/notification system here
    console.error('Cart Error:', message);
    alert(`There was an issue adding items to your cart: ${message}`);
  }

  // Setup input bindings based on data-bind attributes
  // setupInputBindings() {
  //   // Find all inputs with data-bind attributes
  //   const bindableInputs = this.querySelectorAll('[quiz-data-bind]');

  //   bindableInputs.forEach(input => {
  //     const bindTarget = input.getAttribute('quiz-data-bind');
  //     if (bindTarget) {
  //       this.bindInput(input, bindTarget);
  //     }
  //   });
  // }

  setupBindings() {
    const bindableElements = this.querySelectorAll('[quiz-data-bind]');
  
    bindableElements.forEach(el => {
      el.addEventListener('click', () => {
        const updateJSON = el.getAttribute('data-quiz-update');
        // console.log('updateJSON', updateJSON);
        if (!updateJSON) return;
  
        let updates;
        try {
          updates = JSON.parse(updateJSON);
        } catch (err) {
          console.warn('Invalid JSON in data-quiz-update:', err);
          return;
        }
  
        // Apply each update to the quiz element
        for (const [attr, val] of Object.entries(updates)) {
          // console.log('attr', attr, 'val', val);
          this.setAttribute(attr, val);
  
          // Update internal state if needed
          const stateKey = this._toCamel(attr.replace('data-', ''));
          this.state[stateKey] = val;
        }

        // If this is a cross-sell option change, update the price
        if (updates['data-selected-cross-sell-option']) {
          const optionType = updates['data-selected-cross-sell-option'];
          const planId = updates['data-selected-cross-sell-selling-plan-id'] || null;
          this.updateCrossSellPrice(optionType, planId);
        }
  
        // Track analytics
        if (this.analytics) {
          this.analytics.trackInteraction('element_click', {
            updates,
            currentStep: this.state.currentStep
          });
        }
      });
    });
  }

  // Bind an input to a data attribute (state updates automatically via observer)
  // bindInput(input, dataAttributeName) {
  //   // Store the binding
  //   this.bindings.set(input, dataAttributeName);

  //   // Use the data attribute name directly (already includes 'data-' prefix)
  //   const dataAttr = dataAttributeName;
  //   const stateKey = this._toCamel(dataAttributeName.replace('data-', ''));

  //   // Set initial value from state if it exists
  //   if (this.state[stateKey] !== undefined) {
  //     if (input.type === 'radio') {
  //       input.checked = (input.value === this.state[stateKey]);
  //     } else if (input.type === 'checkbox') {
  //       input.checked = Boolean(this.state[stateKey]);
  //     } else {
  //       input.value = this.state[stateKey];
  //     }
  //   }

  //   // Listen for changes and update data attribute (observer will handle state)
  //   input.addEventListener('change', () => {
  //     let value;
  //     if (input.type === 'radio' && input.checked) {
  //       value = input.value;
  //     } else if (input.type === 'checkbox') {
  //       value = input.checked;
  //     } else if (input.type !== 'radio') {
  //       value = input.value;
  //     } else {
  //       return; // Skip unchecked radio buttons
  //     }

  //     // Just update the data attribute - observer will handle state update
  //     this.setAttribute(dataAttr, value);

  //     // Track interaction with analytics
  //     if (this.analytics) {
  //       this.analytics.trackInteraction('input_change', {
  //         inputType: input.type,
  //         binding: dataAttributeName,
  //         value: value,
  //         currentStep: this.state.currentStep
  //       });
  //     }
  //   });
  // }

  // Utility to convert camelCase to kebab-case
  _toKebab(str) {
    return str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
  }

  // Utility to convert kebab-case to camelCase
  _toCamel(str) {
    return str.replace(/-([a-z])/g, g => g[1].toUpperCase());
  }

  // Helper: map a variant name (title) to an ID within this quiz's configured main product
  _mapVariantNameToId(variantTitle) {
    if (!variantTitle) return null;
    const productData = this._getMainProductDataRaw();
    if (!productData) return null;
    try {
      if (!productData || !Array.isArray(productData.variants)) return null;

      // Exact, case-insensitive match first
      const exact = productData.variants.find(v => (v.title || '').toLowerCase().trim() === variantTitle.toLowerCase().trim());
      if (exact) return exact.id;

      // Fallback: includes match (case-insensitive), in case themes differ slightly in formatting
      const loose = productData.variants.find(v => (v.title || '').toLowerCase().includes(variantTitle.toLowerCase().trim()));
      if (loose) return loose.id;
    } catch (e) {
      console.warn('Failed to map variant name to ID:', e);
    }
    return null;
  }

  // Helper: map an ID to a variant name within the quiz's configured main product
  _mapVariantIdToName(variantId) {
    if (!variantId) return null;
    const productData = this._getMainProductDataRaw();
    if (!productData) return null;
    try {
      const found = (productData.variants || []).find(v => v.id.toString() === variantId.toString());
      return found ? found.title : null;
    } catch (e) {
      return null;
    }
  }

  // Helper: get parsed main product JSON embedded in the DOM
  _getMainProductDataRaw() {
    const productElement = this.querySelector('[data-main-product]');
    if (!productElement) return null;
    try {
      return JSON.parse(productElement.textContent);
    } catch (e) {
      return null;
    }
  }

  // Setup cross-sell event listeners and functionality
  setupCrossSell() {
    // Setup radio button listeners for purchase option switching
    const purchaseOptionRadios = this.querySelectorAll('input[data-cross-sell-option]');
    purchaseOptionRadios.forEach((radio) => {
      radio.addEventListener('change', () => this.handlePurchaseOptionChange(radio));
    });

    // Setup selling plan dropdown listeners
    const sellingPlanDropdowns = this.querySelectorAll('select[data-cross-sell-selling-plan]');
    sellingPlanDropdowns.forEach((dropdown) => {
      dropdown.addEventListener('change', () => this.handleSellingPlanChange(dropdown));
    });

    // console.log('setupCrossSell');
    // Initialize cross-sell state with default selection
    this.initializeCrossSellState();

    // Set initial state
    this.updateCrossSellPrice();
  }

  // Initialize cross-sell state with default values
  initializeCrossSellState() {
    // console.log('🔍 Initializing cross-sell state...');
    
    const checkedRadio = this.querySelector('input[data-cross-sell-option]:checked');
          // console.log('🔘 Found checked radio:', checkedRadio);
    
    if (!checkedRadio) {
      // Try to find any cross-sell radio (even if not checked)
      const anyRadio = this.querySelector('input[data-cross-sell-option]');
      // console.log('🔍 Any cross-sell radio found:', anyRadio);
      
      if (!anyRadio) {
        // console.log('⚠️ No cross-sell radios found at all');
        return;
      }
    }
    
    if (checkedRadio) {
      const productId = checkedRadio.getAttribute('data-product-id');
      const variantId = checkedRadio.getAttribute('data-variant-id');
      const optionType = checkedRadio.value;
      const sellingPlanId = checkedRadio.getAttribute('data-selling-plan-id');

      // console.log('📋 Radio button data:', {
      //   productId,
      //   variantId, 
      //   optionType,
      //   sellingPlanId
      // });

      // Set initial state
      this.setAttribute('data-selected-cross-sell-product-id', productId || '');
      this.setAttribute('data-selected-cross-sell-variant-id', variantId || '');
      this.setAttribute('data-selected-cross-sell-option', optionType || '');
      this.setAttribute('data-selected-cross-sell-selling-plan-id', sellingPlanId || '');

      // If subscription is selected and there's a dropdown, get the selected plan
      if (optionType === 'subscription') {
        const dropdown = this.querySelector('select[data-cross-sell-selling-plan]');
        // console.log('📋 Found dropdown:', dropdown);
        if (dropdown && dropdown.value) {
          // console.log('📋 Dropdown value:', dropdown.value);
          this.setAttribute('data-selected-cross-sell-selling-plan-id', dropdown.value);
        }
      }

      // console.log('Cross-sell state initialized:', {
      //   productId: this.state.selectedCrossSellProductId,
      //   variantId: this.state.selectedCrossSellVariantId,
      //   option: this.state.selectedCrossSellOption,
      //   sellingPlanId: this.state.selectedCrossSellSellingPlanId
      // });
    } else {
      // console.log('⚠️ No checked cross-sell radio found');
    }
  }

  // Get current cross-sell state for external use
  getCrossSellState() {
    const state = {
      productId: this.state.selectedCrossSellProductId || undefined,
      variantId: this.state.selectedCrossSellVariantId || undefined,
      option: this.state.selectedCrossSellOption || undefined,
      sellingPlanId: this.state.selectedCrossSellSellingPlanId || undefined,
      isSelected: !!(this.state.selectedCrossSellProductId && this.state.selectedCrossSellVariantId)
    };
    
    // Debug logging
    // console.log('🔍 Current cross-sell state keys in this.state:', {
    //   hasProductId: 'selectedCrossSellProductId' in this.state,
    //   hasVariantId: 'selectedCrossSellVariantId' in this.state,
    //   hasOption: 'selectedCrossSellOption' in this.state,
    //   hasSellingPlanId: 'selectedCrossSellSellingPlanId' in this.state,
    //   fullStateKeys: Object.keys(this.state)
    // });
    
    return state;
  }

  // Handle purchase option change (subscription vs onetime)
  handlePurchaseOptionChange(radio) {
    if (!radio.checked) return;

    const optionType = radio.value; // 'subscription' or 'onetime'
    const productId = radio.getAttribute('data-product-id');
    const variantId = radio.getAttribute('data-variant-id');
    const sellingPlanId = radio.getAttribute('data-selling-plan-id');

    // Update quiz state
    this.setAttribute('data-selected-cross-sell-product-id', productId);
    this.setAttribute('data-selected-cross-sell-variant-id', variantId);
    this.setAttribute('data-selected-cross-sell-option', optionType);
    
    if (optionType === 'subscription' && sellingPlanId) {
      this.setAttribute('data-selected-cross-sell-selling-plan-id', sellingPlanId);
    } else {
      this.setAttribute('data-selected-cross-sell-selling-plan-id', '');
    }

    // console.log('Cross-sell state updated:', {
    //   productId: this.state.selectedCrossSellProductId,
    //   variantId: this.state.selectedCrossSellVariantId,
    //   option: this.state.selectedCrossSellOption,
    //   sellingPlanId: this.state.selectedCrossSellSellingPlanId
    // });

    // Update price display
    this.updateCrossSellPrice(optionType);


    // Track analytics if available
    if (this.analytics) {
      this.analytics.trackInteraction('cross_sell_option_change', {
        optionType,
        productId,
        variantId,
        currentStep: this.state.currentStep
      });
    }
  }

  // Handle selling plan dropdown change
  handleSellingPlanChange(dropdown) {
    const selectedPlanId = dropdown.value;
    const selectedOption = dropdown.options[dropdown.selectedIndex];
    const planName = selectedOption.getAttribute('data-plan-name');

    // Update quiz state with new selling plan through data attribute system
    this.setAttribute('data-selected-cross-sell-selling-plan-id', selectedPlanId);

    // Also trigger the update through the existing binding system for consistency
    this.dispatchEvent(new CustomEvent('state-changed', {
      detail: { 
        key: 'selectedCrossSellSellingPlanId', 
        value: selectedPlanId, 
        dataAttribute: 'data-selected-cross-sell-selling-plan-id'
      }
    }));

    // console.log('Cross-sell selling plan updated via dropdown:', {
    //   planId: selectedPlanId,
    //   planName: planName,
    //   fullState: this.getCrossSellState()
    // });

    // Update price with discount if applicable
    this.updateCrossSellPrice('subscription', selectedPlanId);

    // Update discount badge
    this.updateDiscountBadge(selectedPlanId);

    // Track analytics if available
    if (this.analytics) {
      this.analytics.trackInteraction('cross_sell_plan_change', {
        planId: selectedPlanId,
        planName,
        currentStep: this.state.currentStep
      });
    }
  }

  // Update cross-sell price display
  updateCrossSellPrice(optionType = null, planId = null) {
    const priceElement = this.querySelector('[data-cross-sell-price]');
    if (!priceElement) return;

    // Determine current option type if not provided
    if (!optionType) {
      const checkedOption = this.querySelector('input[data-cross-sell-option]:checked');
      optionType = checkedOption ? checkedOption.value : 'subscription';
    }

    // Get base prices from data attributes (these are in cents)
    const onetimePrice = parseInt(priceElement.getAttribute('data-onetime-price')) || 0;
    const subscriptionPrice = parseInt(priceElement.getAttribute('data-subscription-price')) || 0;

    let finalPrice;

    if (optionType === 'onetime') {
      finalPrice = onetimePrice;
    } else if (optionType === 'subscription') {
      // Start with base subscription price
      finalPrice = subscriptionPrice;
      
      // Apply additional discount if a specific plan is selected
      if (planId && this.crossSellData) {
        const plan = this.crossSellData.sellingPlans.find(p => p.id.toString() === planId);
        if (plan && plan.priceAdjustments && plan.priceAdjustments.length > 0) {
          const adjustment = plan.priceAdjustments[0];
          if (adjustment.type === 'percentage') {
            finalPrice = onetimePrice * (1 - adjustment.value / 100);
          } else if (adjustment.type === 'fixed_amount') {
            finalPrice = onetimePrice - adjustment.value;
          }
        }
      }
    } else {
      // Fallback to subscription price
      finalPrice = subscriptionPrice;
    }

    // Format and display price with currency symbol
    const formattedPrice = this.formatPrice(finalPrice);
    priceElement.textContent = formattedPrice;

    // console.log('💰 Price updated:', {
    //   optionType,
    //   planId,
    //   onetimePrice,
    //   subscriptionPrice,
    //   finalPrice,
    //   formattedPrice
    // });
  }

  // Update discount badge based on selected plan
  updateDiscountBadge(planId) {
    if (!this.crossSellData || !planId) return;

    const discountBadge = this.querySelector('[data-discount-badge]');
    if (!discountBadge) return;

    const plan = this.crossSellData.sellingPlans.find(p => p.id.toString() === planId);
    
    if (plan && plan.priceAdjustments && plan.priceAdjustments.length > 0) {
      const adjustment = plan.priceAdjustments[0];
      if (adjustment.value > 0) {
        discountBadge.textContent = `${Math.round(adjustment.value)}% Off`;
        discountBadge.style.display = 'inline-block';
      } else {
        discountBadge.style.display = 'none';
      }
    } else {
      discountBadge.style.display = 'none';
    }
  }

  // Format price from cents to currency string
  formatPrice(priceInCents) {
    const price = priceInCents / 100;
    return price.toFixed(2);
  }

  // Setup cross-sell for current step
  setupCrossSellForCurrentStep() {
    // console.log('🔧 Setting up cross-sell for current step...');
    
    // Setup radio button listeners for purchase option switching (current step only)
    const purchaseOptionRadios = this.querySelectorAll('input[data-cross-sell-option]');
          // console.log('🔘 Purchase option radios found:', purchaseOptionRadios.length);
    
    purchaseOptionRadios.forEach((radio) => {
      // Remove existing listeners to avoid duplicates
      radio.removeEventListener('change', this.handlePurchaseOptionChange);
      // Add new listener
      radio.addEventListener('change', () => this.handlePurchaseOptionChange(radio));
    });

    // Setup selling plan dropdown listeners (current step only)
    const sellingPlanDropdowns = this.querySelectorAll('select[data-cross-sell-selling-plan]');
          // console.log('📋 Selling plan dropdowns found:', sellingPlanDropdowns.length);
    
    sellingPlanDropdowns.forEach((dropdown) => {
      // Remove existing listeners to avoid duplicates
      dropdown.removeEventListener('change', this.handleSellingPlanChange);
      // Add new listener
      dropdown.addEventListener('change', () => this.handleSellingPlanChange(dropdown));
    });

    // Initialize cross-sell state
    this.initializeCrossSellState();

    // Update cross-sell price
    this.updateCrossSellPrice();
    
          // console.log('✅ Cross-sell setup complete for current step');
  }

  // === ANNUAL UPSELL FUNCTIONALITY ===

  // Handle annual upsell toggle
  toggleAnnualUpsell() {
    this.state.bladeShipsOpen = !this.state.bladeShipsOpen;
    
    // Update the blade ships container class (this triggers the background color change)
    const bladeShipsContainer = this.querySelector('[data-ships-later-container]');
    if (bladeShipsContainer) {
      bladeShipsContainer.classList.toggle('open', this.state.bladeShipsOpen);
    }
    
    // The CSS handles the arrow rotation and content expansion automatically
    // through the .open class on the container, so no individual element manipulation needed
    
    // console.log('🔄 Annual upsell toggled:', this.state.bladeShipsOpen ? 'open' : 'closed');
  }

  // Handle switching to annual plan
  switchToAnnual() {
    // console.log('🔄 Switching to annual plan...');
    
    // Update state
    this.state.yearlyBladesSelected = true;
    this.state.bladeShipsOpen = false;
    
    // Close any open upsell sections
    const bladeShipsContainer = this.querySelector('[data-ships-later-container]');
    if (bladeShipsContainer) {
      bladeShipsContainer.classList.remove('open');
    }
    
    // Re-render the recap with annual plan
    this.renderRecap();
    
    // console.log('✅ Switched to annual plan - blades moved to Ships Now');
  }

  // Get annual blade info for upsell calculations
  getAnnualBladeInfo() {
    const annualProductElement = this.querySelector('[data-annual-product]');
    if (!annualProductElement) {
      console.warn('No annual product data found');
      return null;
    }

    try {
      const annualProductData = JSON.parse(annualProductElement.textContent);
      const annualVariant = annualProductData.annualVariant;
      
      if (!annualVariant) {
        console.warn('No annual variant found');
        return null;
      }

      // Find the 12-month selling plan allocation
      const annualAllocation = annualVariant.selling_plan_allocations.find(allocation => {
        const planName = allocation.selling_plan.name.toLowerCase();
        return planName.includes('12 month') || planName.includes('annual') || planName.includes('year');
      });

      if (!annualAllocation) {
        console.warn('No annual allocation found');
        return null;
      }

      // Extract blade count from variant title
      let bladeCount = 24; // default for annual
      const variantTitle = annualVariant.title.toLowerCase();
      const bladeMatch = variantTitle.match(/(\d+)\s*blades?/);
      if (bladeMatch) {
        bladeCount = parseInt(bladeMatch[1]);
      }

      return {
        title: `${annualProductData.title} x${bladeCount}`,
        subtitle: `Ships every 12 months`,
        image: annualVariant.featured_image || annualProductData.featured_image || annualVariant.bottom_aligned_card,
        price: annualAllocation.price,
        quantity: 1,
        sellingPlan: annualAllocation.selling_plan,
        monthFrequency: 12,
        isAnnual: true,
        bladeCount: bladeCount
      };
    } catch (e) {
      console.warn('Failed to parse annual product data:', e);
      return null;
    }
  }

  // Calculate annual savings
  calculateAnnualSavings() {
    const currentBladeInfo = this.getBladeShipInfo();
    const annualBladeInfo = this.getAnnualBladeInfo();
    
    if (!currentBladeInfo || !annualBladeInfo) {
      return null;
    }

    // Calculate cost per year for current plan
    const monthsPerYear = 12;
    const currentDeliveries = monthsPerYear / currentBladeInfo.monthFrequency;
    const currentYearlyCost = currentBladeInfo.price * currentDeliveries;
    
    // Annual plan cost
    const annualCost = annualBladeInfo.price;
    
    // Savings
    const savings = currentYearlyCost - annualCost;
    
    return {
      savings: savings,
      currentYearlyCost: currentYearlyCost,
      annualCost: annualCost,
      formattedSavings: this.formatPrice(savings)
    };
  }

  // === RECAP FUNCTIONALITY ===

    // Build cart data for recap display
  buildRecapCart() {
    const cart = [];
    const isAnnualSelected = this.state.annualProductSelected === 'true' || this.state.yearlyBladesSelected;
    const isBladesQuiz = this.id === 'blades_quiz';
    
    // console.log('🛒 Building recap cart:', {
    //   isAnnualSelected,
    //   annualProductSelected: this.state.annualProductSelected,
    //   yearlyBladesSelected: this.state.yearlyBladesSelected,
    //   isBladesQuiz: isBladesQuiz,
    //   quizId: this.id
    // });
    
    // Main product (razor handle)
    // For Blades Quiz: Skip main product if annual is selected (only want annual blades)
    const shouldAddMainProduct = isBladesQuiz ? !isAnnualSelected : true;
    
    if (this.state.selectedMainVariantId && shouldAddMainProduct) {
      const mainProductData = this.getMainProductData();
      if (mainProductData) {
        // For annual plans, razor doesn't need selling plan (blades handle the subscription)
        const razorSellingPlan = isAnnualSelected ? null : mainProductData.sellingPlan;
        
        cart.push({
          type: 'main',
          title: mainProductData.title,
          subtitle: mainProductData.variantTitle,
          image: mainProductData.image,
          price: mainProductData.price,
          quantity: 1,
          sellingPlan: razorSellingPlan,
          shipsNow: true,
          variantId: this.state.selectedMainVariantId
        });
        
        // console.log('✅ Added main product to cart (non-blades quiz or non-annual)');
      }
    } else if (isBladesQuiz && isAnnualSelected) {
      // console.log('⏭️ Skipping main product for Blades Quiz with annual selection - only annual blades will be added');
    }

    // Cross-sell product
    const crossSellState = this.getCrossSellState();
    // console.log('🛒 Recap - Cross-sell state:', crossSellState);
    
    if (crossSellState.isSelected) {
      // console.log('✅ Cross-sell is selected, getting product data...');
      const crossSellData = this.getCrossSellProductData();
      // console.log('📦 Cross-sell product data:', crossSellData);
      
      if (crossSellData) {
        const crossSellItem = {
          type: 'cross-sell',
          title: crossSellData.title,
          subtitle: crossSellData.subtitle,
          image: crossSellData.image,
          price: crossSellData.price,
          quantity: 1,
          sellingPlan: crossSellData.sellingPlan,
          shipsNow: true,
          variantId: this.state.selectedCrossSellVariantId
        };
        cart.push(crossSellItem);
        // console.log('✅ Added cross-sell to cart:', crossSellItem);
      } else {
        console.warn('⚠️ Cross-sell selected but no product data found');
      }
    } else {
      // console.log('❌ Cross-sell not selected for recap');
    }

    // Blade refills - ships now for annual, ships later for non-annual
    if (isAnnualSelected) {
      // Annual plan: blades ship now
      const annualBladeInfo = this.getAnnualBladeInfo();
      if (annualBladeInfo) {
        cart.push({
          type: 'blades',
          title: annualBladeInfo.title,
          subtitle: annualBladeInfo.subtitle,
          image: annualBladeInfo.image,
          price: annualBladeInfo.price,
          quantity: 1,
          sellingPlan: annualBladeInfo.sellingPlan,
          shipsNow: true,
          monthFrequency: annualBladeInfo.monthFrequency,
          variantId: this.state.annualVariantId
        });
      }
    } else {
      // Non-annual plan: blades ship later
      const bladeShipInfo = this.getBladeShipInfo();
      if (bladeShipInfo) {
        cart.push({
          type: 'blades',
          title: bladeShipInfo.title,
          subtitle: bladeShipInfo.subtitle,
          image: bladeShipInfo.image,
          price: bladeShipInfo.price,
          quantity: 1,
          sellingPlan: bladeShipInfo.sellingPlan,
          shipsNow: false,
          monthFrequency: bladeShipInfo.monthFrequency,
          variantId: this.state.swapVariantId
        });
      }
    }

    return cart;
  }

  // Get main product data based on current selections
  getMainProductData() {
    if (!this.state.selectedMainVariantId) return null;

    const productElement = this.querySelector('[data-main-product]');
    if (!productElement) return null;

    try {
      const productData = JSON.parse(productElement.textContent);
      const selectedVariant = productData.variants.find(v => v.id.toString() === this.state.selectedMainVariantId);
      
      if (!selectedVariant) return null;

      let sellingPlan = null;
      if (this.state.selectedMainSellingPlanId) {
        // Find the selling plan
        for (const group of productData.selling_plan_groups || []) {
          const plan = group.selling_plans.find(p => p.id.toString() === this.state.selectedMainSellingPlanId);
          if (plan) {
            sellingPlan = plan;
            break;
          }
        }
      }

      const rawVariantTitle = (selectedVariant.title || '').trim();
      const variantTitle = rawVariantTitle.toLowerCase() === 'default title' ? '' : rawVariantTitle;

      return {
        title: productData.title,
        variantTitle: variantTitle,
        image: selectedVariant.featured_image || productData.featured_image || selectedVariant.bottom_aligned_card,
        price: selectedVariant.price,
        sellingPlan: sellingPlan
      };
    } catch (e) {
      console.warn('Failed to parse main product data:', e);
      return null;
    }
  }

  // Get cross-sell product data based on current selections
  getCrossSellProductData() {
    const crossSellState = this.getCrossSellState();
    if (!crossSellState.isSelected) {
      // console.log('❌ Cross-sell not selected, returning null');
      return null;
    }

    const crossSellDataElement = this.querySelector('[data-cross-sell-data]');

    const crossSellData = JSON.parse(crossSellDataElement.textContent);
    // console.log('📊 Raw cross-sell data:', crossSellData);
    
    // Calculate price based on option and selling plan
    let price = crossSellState.option === 'subscription' ? crossSellData.subscriptionPrice : crossSellData.onetimePrice;
    let subtitle = '';
    let sellingPlan = null;

    if (crossSellState.option === 'subscription' && crossSellState.sellingPlanId) {
      const plan = crossSellData.sellingPlans.find(p => p.id.toString() === crossSellState.sellingPlanId);
      if (plan) {
        sellingPlan = plan;
        subtitle = `Ships every ${plan.name.toLowerCase()}`;
      }
    }

    // Try to get product title and image from cross-sell element (if available)
    const titleElement = this.querySelector('[data-cross-sell-title]');
    const imageElement = this.querySelector('.quiz-cross-sell__upsell-image img');
    
    // Fallback to getting product title from the main product data based on productId
    let title = 'Cross-sell Product';
    let image = null;
    
    if (titleElement) {
      title = titleElement.textContent.split(' - ')[0];
    } else {
      // Try to construct title from JSON data if DOM element not available
      // This would require the product title to be in the cross-sell JSON data
      title = crossSellData.productTitle || `Product ${crossSellData.productId}`;
    }
    
    if (imageElement) {
      image = imageElement.src;
    } else {
      // Try to get image from JSON data if DOM element not available
      image = crossSellData.productImage || null;
    }

    const result = {
      title: title,
      subtitle: subtitle,
      image: image,
      price: price,
      sellingPlan: sellingPlan
    };
    
    // console.log('✅ Built cross-sell product data:', result);
    return result;
  }

  // Get blade shipping information
  getBladeShipInfo() {
    // Check if annual product is NOT selected (meaning non-annual blade subscription)
    if (this.state.annualProductSelected === 'false' && this.state.selectedMainSellingPlanId) {
      // Get swap product data for blade refills
      const swapProductElement = this.querySelector('[data-swap-product]');
      if (!swapProductElement) {
        console.warn('No swap product data found');
        return null;
      }

      try {
        const swapProductData = JSON.parse(swapProductElement.textContent);
        const swapVariant = swapProductData.swapVariant;
        
        if (!swapVariant) {
          console.warn('No swap variant found');
          return null;
        }

        // Find the selling plan allocation that matches the selected main selling plan
        const matchingAllocation = swapVariant.selling_plan_allocations.find(allocation => 
          allocation.selling_plan_id.toString() === this.state.selectedMainSellingPlanId
        );

        if (!matchingAllocation) {
          console.warn('No matching selling plan allocation found for selected plan:', this.state.selectedMainSellingPlanId);
          // Try to find a selling plan with similar frequency from the swap product
          const mainProductElement = this.querySelector('[data-main-product]');
          if (!mainProductElement) return null;
          
          try {
            const mainProductData = JSON.parse(mainProductElement.textContent);
            const selectedMainPlan = mainProductData.selling_plan_groups
              .flatMap(group => group.selling_plans)
              .find(plan => plan.id.toString() === this.state.selectedMainSellingPlanId);
            
            if (!selectedMainPlan) return null;
            
            // Extract frequency from main plan and find corresponding swap plan
            let monthFrequency = 2;
            const planName = selectedMainPlan.name.toLowerCase();
            if (planName.includes('2 months') || planName.includes('2-month')) {
              monthFrequency = 2;
            } else if (planName.includes('3 months') || planName.includes('3-month')) {
              monthFrequency = 3;
            } else if (planName.includes('4 months') || planName.includes('4-month')) {
              monthFrequency = 4;
            }
            
            // Find a swap product selling plan with similar frequency
            const similarSwapPlan = swapProductData.selling_plan_groups
              .flatMap(group => group.selling_plans)
              .find(plan => {
                const swapPlanName = plan.name.toLowerCase();
                return swapPlanName.includes(`${monthFrequency} month`);
              });
              
            if (similarSwapPlan) {
              // Create a fallback allocation using swap variant price
              const fallbackAllocation = {
                selling_plan_id: similarSwapPlan.id,
                price: swapVariant.price,
                selling_plan: similarSwapPlan
              };
              // console.log('🔄 Using fallback allocation with similar frequency:', fallbackAllocation);
              return this.buildBladeShipInfo(swapProductData, swapVariant, fallbackAllocation);
            }
          } catch (e) {
            console.warn('Failed to find fallback allocation:', e);
          }
          
          return null;
        }

        return this.buildBladeShipInfo(swapProductData, swapVariant, matchingAllocation);
      } catch (e) {
        console.warn('Failed to parse swap product data:', e);
        return null;
      }
    }

    return null;
  }

  // Helper method to build blade ship info from swap product data
  buildBladeShipInfo(swapProductData, swapVariant, allocation) {
    // Extract blade count from variant title (e.g., "8 Blades" -> 8)
    let bladeCount = 8; // default
    const variantTitle = swapVariant.title.toLowerCase();
    const bladeMatch = variantTitle.match(/(\d+)\s*blades?/);
    if (bladeMatch) {
      bladeCount = parseInt(bladeMatch[1]);
    }

    // Extract frequency from selling plan name
    let monthFrequency = 2; // default
    const planName = allocation.selling_plan.name.toLowerCase();
    if (planName.includes('2 months') || planName.includes('2-month')) {
      monthFrequency = 2;
    } else if (planName.includes('3 months') || planName.includes('3-month')) {
      monthFrequency = 3;
    } else if (planName.includes('4 months') || planName.includes('4-month')) {
      monthFrequency = 4;
    }

    // Use real price from the selling plan allocation
    const realPrice = allocation.price;

    // console.log('🔪 Real blade ship info:', {
    //   title: `${swapProductData.title} x${bladeCount}`,
    //   variantTitle: swapVariant.title,
    //   price: realPrice,
    //   monthFrequency: monthFrequency,
    //   sellingPlan: allocation.selling_plan
    // });

    return {
      title: `${swapProductData.title} x${bladeCount}`,
      subtitle: `Ships every ${monthFrequency} months for ${this.formatPrice(realPrice)}`,
      image: swapVariant.featured_image || swapProductData.featured_image || swapVariant.bottom_aligned_card,
      price: realPrice,
      quantity: 1,
      sellingPlan: allocation.selling_plan,
      monthFrequency: monthFrequency,
      isAnnual: false
    };
  }

  // Calculate line price including selling plan discounts
  calculateLinePrice(cartItem) {
    let price = cartItem.price;
    
    if (cartItem.sellingPlan && cartItem.sellingPlan.priceAdjustments) {
      const adjustment = cartItem.sellingPlan.priceAdjustments[0];
      if (adjustment && adjustment.value > 0) {
        if (adjustment.type === 'percentage') {
          price = cartItem.price * (1 - adjustment.value / 100);
        } else if (adjustment.type === 'fixed_amount') {
          price = cartItem.price - adjustment.value;
        }
      }
    }
    
    return price * (cartItem.quantity || 1);
  }

  // Format money using theme.js formatMoney function with proper currency format
  formatMoney(cents, format = "") {
    if (typeof cents === "string") {
      cents = cents.replace(/[.,]/g, "");
    }

    // Convert string to number
    cents = parseFloat(cents);

    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/, formatString = format || window.themeVariables?.settings?.moneyFormat || "$ {{amount}}";

    function defaultTo(value, defaultValue) {
      return value == null || value !== value ? defaultValue : value;
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultTo(precision, 2);
      thousands = defaultTo(thousands, ",");
      decimal = defaultTo(decimal, ".");

      if (isNaN(number) || number == null) {
        return 0;
      }

      number = (number / 100).toFixed(precision);
      let parts = number.split("."), dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands), centsAmount = parts[1] ? decimal + parts[1] : "";
      return dollarsAmount + centsAmount;
    }

    let value = "";
    switch (formatString.match(placeholderRegex)[1]) {
      case "amount":
        value = formatWithDelimiters(cents, 2);
        break;
      case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0);
        break;
      case "amount_with_space_separator":
        value = formatWithDelimiters(cents, 2, " ", ".");
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_with_apostrophe_separator":
        value = formatWithDelimiters(cents, 2, "'", ".");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ".", ",");
        break;
      case "amount_no_decimals_with_space_separator":
        value = formatWithDelimiters(cents, 0, " ");
        break;
      case "amount_no_decimals_with_apostrophe_separator":
        value = formatWithDelimiters(cents, 0, "'");
        break;
    }

    if (formatString.indexOf("with_comma_separator") !== -1) {
      return formatString.replace(placeholderRegex, value);
    } else {
      return formatString.replace(placeholderRegex, value);
    }
  }

  // Get proper currency format from theme variables
  getCurrencyFormat() {
    return window.themeVariables?.settings?.currencyCodeEnabled 
      ? window.themeVariables.settings.moneyWithCurrencyFormat 
      : window.themeVariables?.settings?.moneyFormat || "$ {{amount}}";
  }

  // Helper method to format price with proper currency
  formatPrice(priceInCents) {
    return this.formatMoney(priceInCents, this.getCurrencyFormat());
  }

  // Render the recap content
  renderRecap() {
    const cart = this.buildRecapCart();
    // console.log('🛒 Built recap cart:', cart);

    // Get containers
    const shipsNowContainer = this.querySelector('[data-ships-now-container]');
    const shipsLaterContainer = this.querySelector('[data-ships-later-container]');
    const totalPriceElement = this.querySelector('[data-recap-total-price]');

    if (!shipsNowContainer || !totalPriceElement) {
      console.warn('Recap containers not found');
      return;
    }

    // Filter items by shipping timing
    const shipsNowItems = cart.filter(item => item.shipsNow);
    const shipsLaterItems = cart.filter(item => !item.shipsNow);

    // Render ships now items
    this.renderCartLines(shipsNowContainer, shipsNowItems);

    // Handle ships later section
    if (shipsLaterItems.length > 0 && shipsLaterContainer) {
      shipsLaterContainer.style.display = 'block';
      const bladeShipsLine = shipsLaterContainer.querySelector('[data-blade-ships-line]');
      if (bladeShipsLine) {
        this.renderBladeShipsLine(bladeShipsLine, shipsLaterItems[0]);
        
        // Setup annual upsell if non-annual blades are present
        this.setupAnnualUpsell();
      }
    } else if (shipsLaterContainer) {
      shipsLaterContainer.style.display = 'none';
    }

    // Calculate and display total
    const totalPrice = shipsNowItems.reduce((total, item) => total + this.calculateLinePrice(item), 0);
    totalPriceElement.textContent = this.formatPrice(totalPrice);

    // Setup California subscription terms checkbox
    this.setupCaliforniaTerms();
  }

  // Render cart lines for ships now items
  renderCartLines(container, items) {
    container.innerHTML = '';

    items.forEach(item => {
      const lineElement = document.createElement('div');
      lineElement.className = 'quiz-recap__cart-line';
      
      const linePrice = this.calculateLinePrice(item);
      
      lineElement.innerHTML = `
        <div class="quiz-recap__cart-line-image">
          ${item.image ? `<img src="${item.image}" alt="${item.title}" />` : ''}
        </div>
        <div class="quiz-recap__cart-line-info">
          <div class="quiz-recap__cart-line-title">
            <span>${item.title}</span>
            <span>${this.formatPrice(linePrice)}</span>
          </div>
          <div class="quiz-recap__cart-line-subtitle">
            ${item.subtitle || ''}
          </div>
        </div>
      `;
      
      container.appendChild(lineElement);
    });
  }

  // Render blade ships line for ships later items
  renderBladeShipsLine(container, bladeItem) {
    const linePrice = this.calculateLinePrice(bladeItem);
    
    // Check if annual upsell should be available
    const annualBladeInfo = this.getAnnualBladeInfo();
    const savings = this.calculateAnnualSavings();
    const showAnnualUpsell = annualBladeInfo && savings && savings.savings > 0;
    
    // Build annual upsell HTML if available
    let annualUpsellHTML = '';
    if (showAnnualUpsell) {
      annualUpsellHTML = `
        <div class="quiz-recap__blade-ships-line-save-toggle" data-annual-upsell-toggle>
          <span>Want to save more on blades?</span>
          <span class="quiz-recap__blade-ships-line-save-toggle-arrow"></span>
        </div>
        <div class="quiz-recap__blade-ships-line-save" data-annual-upsell-content>
          <div class="quiz-recap__blade-ships-line-save-text">
            Save up to ${savings.formattedSavings} when you pay upfront for one annual shipment. Update your plan to bulk purchase one annual shipment of blades!
          </div>
          <div class="quiz-recap__blade-ships-line-save-button" data-annual-upsell-button>
            Save now
          </div>
        </div>
      `;
    }
    
    container.innerHTML = `
      <div class="quiz-recap__blade-ships-line-image">
        ${bladeItem.image ? `<img src="${bladeItem.image}" alt="${bladeItem.title}" />` : ''}
      </div>
      <div class="quiz-recap__blade-ships-line-info">
        <div class="quiz-recap__blade-ships-line-title">
          ${bladeItem.title}
        </div>
        <div class="quiz-recap__blade-ships-line-subtitle">
          Ships every ${bladeItem.monthFrequency} months for ${this.formatPrice(linePrice)}
        </div>
        ${annualUpsellHTML}
      </div>
    `;
    
    // Setup event listeners for annual upsell if present
    if (showAnnualUpsell) {
      this.setupAnnualUpsellListeners(container);
    }
  }

  // Setup annual upsell event listeners for a specific container
  setupAnnualUpsellListeners(container) {
    const toggle = container.querySelector('[data-annual-upsell-toggle]');
    const content = container.querySelector('[data-annual-upsell-content]');
    const button = container.querySelector('[data-annual-upsell-button]');

    if (!toggle || !content || !button) {
      console.warn('Annual upsell elements not found in container');
      return;
    }

    // Initial state is handled by CSS max-height: 0, no JS manipulation needed
    // The container's .open class controls all animations and states

    // Add event listeners
    toggle.addEventListener('click', (e) => {
      e.preventDefault();
      this.toggleAnnualUpsell();
    });

    button.addEventListener('click', (e) => {
      e.preventDefault();
      this.switchToAnnual();
    });

    // console.log('✅ Annual upsell listeners setup complete');
  }

  // Setup annual upsell functionality (now simplified)
  setupAnnualUpsell() {
    // This method is now primarily called from renderRecap for any additional setup
    // The main setup is done in renderBladeShipsLine and setupAnnualUpsellListeners
    // console.log('📋 Annual upsell setup called');
  }

  // ----------------------------------------------------------------------------------------------------------------
  // CALIFORNIA SUBSCRIPTION TERMS CHECKBOX FUNCTIONALITY
  // ----------------------------------------------------------------------------------------------------------------

  // Setup California terms checkbox
  setupCaliforniaTerms() {
    const caTermsContainer = this.querySelector('[data-ca-subscription-terms]');
    const caTermsLabel = this.querySelector('[data-ca-terms-label]');
    
    if (!caTermsContainer || !caTermsLabel) {
      return;
    }

    // Check if we should show the checkbox (California resident + has subscription)
    const hasSubscription = this.hasActiveSubscription();
    
    if (this.isCaliforniaResident && hasSubscription) {
      caTermsContainer.style.display = 'block';
      this.updateCaTermsDisplay();
      
      // Add click listener to toggle checkbox
      caTermsLabel.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleCaTerms();
      });
    } else {
      caTermsContainer.style.display = 'none';
    }
  }

  // Check if current cart has an active subscription
  hasActiveSubscription() {
    const cart = this.buildRecapCart();
    // Check if any item has a selling plan (subscription)
    return cart.some(item => item.sellingPlan && item.sellingPlan.id);
  }

  // Toggle California terms checkbox
  toggleCaTerms() {
    this.caTermsAgreed = !this.caTermsAgreed;
    
    const checkbox = this.querySelector('[data-ca-terms-checkbox]');
    const container = this.querySelector('[data-ca-subscription-terms]');
    
    if (checkbox) {
      if (this.caTermsAgreed) {
        checkbox.classList.add('checked');
        container.classList.remove('quiz-recap__ca-terms--error');
      } else {
        checkbox.classList.remove('checked');
      }
    }

    // Update button state
    this.updateAddToCartButtonState();
  }

  // Update California terms display with price and frequency
  updateCaTermsDisplay() {
    const priceElement = this.querySelector('[data-ca-terms-price]');
    const frequencyElement = this.querySelector('[data-ca-terms-frequency]');
    
    if (!priceElement || !frequencyElement) return;
    
    // Get blade info for subscription details
    const bladeInfo = this.getBladeShipInfo();
    
    if (bladeInfo) {
      priceElement.textContent = this.formatPrice(bladeInfo.price);
      frequencyElement.textContent = `${bladeInfo.monthFrequency} months`;
    } else {
      // Fallback to main product selling plan info
      const mainProductData = this.getMainProductData();
      if (mainProductData && mainProductData.sellingPlan) {
        const planName = mainProductData.sellingPlan.name || '';
        // Extract frequency from plan name (e.g., "2 Months" -> "2 months")
        frequencyElement.textContent = planName.toLowerCase();
        priceElement.textContent = this.formatPrice(mainProductData.price);
      }
    }
  }

  // Validate California terms before adding to cart
  validateCaTerms() {
    const caTermsContainer = this.querySelector('[data-ca-subscription-terms]');
    
    // If container is not visible, no validation needed
    if (!caTermsContainer || caTermsContainer.style.display === 'none') {
      return true;
    }
    
    // Check if California resident and has subscription
    if (this.isCaliforniaResident && this.hasActiveSubscription()) {
      if (!this.caTermsAgreed) {
        // Show error state
        caTermsContainer.classList.add('quiz-recap__ca-terms--error');
        
        // Scroll to checkbox
        caTermsContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
        
        return false;
      }
    }
    
    return true;
  }

  // Update Add to Cart button state based on CA terms
  updateAddToCartButtonState() {
    const addToCartButton = this.querySelector('.quiz-next-button button');
    
    if (!addToCartButton) return;
    
    const caTermsContainer = this.querySelector('[data-ca-subscription-terms]');
    const isVisible = caTermsContainer && caTermsContainer.style.display !== 'none';
    
    if (isVisible && this.isCaliforniaResident && this.hasActiveSubscription() && !this.caTermsAgreed) {
      addToCartButton.classList.add('quiz-recap__button--disabled');
      addToCartButton.disabled = true;
    } else {
      addToCartButton.classList.remove('quiz-recap__button--disabled');
      addToCartButton.disabled = false;
    }
  }
  }

  customElements.define('generic-quiz', GenericQuiz);
}

// Only define GenericQuizOpenButton if it hasn't been defined yet
if (typeof customElements.get('generic-quiz-open-button') === 'undefined') {
  class GenericQuizOpenButton extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    // Attach click listener - the actual button inside handles focus and semantics
    this.addEventListener('click', this.openQuiz.bind(this));
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.openQuiz.bind(this));
  }

    openQuiz() {
      const quizName = this.getAttribute('data-quiz-name');
      const variantId = this.getAttribute('data-selected-variant-id') || undefined;
      const quizElement = document.getElementById(quizName);
      if (quizElement && typeof quizElement.open === 'function') {
        quizElement.open({ variantId });
      } else {
        console.warn(`Quiz element not found or missing .open():`, quizName);
      }
    }
  }

  customElements.define('generic-quiz-open-button', GenericQuizOpenButton);
}